export const PROFILE_AVATARS_BUCKET = 'profile-avatars';
export const PROFILE_AVATAR_ACCEPT = 'image/jpeg,image/png,image/webp';

const PROFILE_AVATAR_MAX_BYTES = 5 * 1024 * 1024;
const PROFILE_AVATAR_ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

const PROFILE_AVATAR_EXTENSION_BY_MIME_TYPE: Record<(typeof PROFILE_AVATAR_ALLOWED_MIME_TYPES)[number], string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

const PROFILE_AVATAR_MIME_TYPE_BY_EXTENSION: Record<string, (typeof PROFILE_AVATAR_ALLOWED_MIME_TYPES)[number]> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
};

export function validateProfileAvatarFile(file: File) {
  if (!getProfileAvatarMimeType(file)) {
    return 'Upload a JPG, PNG, or WebP profile photo.';
  }

  if (file.size > PROFILE_AVATAR_MAX_BYTES) {
    return 'Profile photo must be 5 MB or smaller.';
  }

  return null;
}

export function getProfileAvatarMimeType(file: File) {
  const mimeType = file.type.toLowerCase();
  if (PROFILE_AVATAR_ALLOWED_MIME_TYPES.includes(mimeType as (typeof PROFILE_AVATAR_ALLOWED_MIME_TYPES)[number])) {
    return mimeType as (typeof PROFILE_AVATAR_ALLOWED_MIME_TYPES)[number];
  }

  const extension = getFileExtension(file.name);
  return extension ? PROFILE_AVATAR_MIME_TYPE_BY_EXTENSION[extension] ?? null : null;
}

export function buildProfileAvatarStoragePath(userId: string, file: File) {
  const mimeType = getProfileAvatarMimeType(file);
  const extension = mimeType ? PROFILE_AVATAR_EXTENSION_BY_MIME_TYPE[mimeType] : getFileExtension(file.name) ?? 'jpg';
  const id = createStorageId();

  return `${userId}/${id}.${extension}`;
}

export function extractProfileAvatarStoragePath(publicUrl: string | null | undefined, userId: string) {
  if (!publicUrl) return null;

  const marker = `/object/public/${PROFILE_AVATARS_BUCKET}/`;
  const markerIndex = publicUrl.indexOf(marker);
  if (markerIndex === -1) return null;

  const rawPath = publicUrl.slice(markerIndex + marker.length).split('?')[0];
  let decodedPath: string;
  try {
    decodedPath = decodeURIComponent(rawPath);
  } catch {
    return null;
  }

  return decodedPath.startsWith(`${userId}/`) ? decodedPath : null;
}

function getFileExtension(fileName: string) {
  const extension = fileName.split('.').pop()?.trim().toLowerCase();
  return extension || null;
}

function createStorageId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
